import type { DriveStep } from 'driver.js';
import { useCallback, useMemo } from 'react';
import { useManualDriver } from '/@/components/driverjs/useDriver';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { GROWERS_HELP_TOUR_IDS } from '../growers-help-tour-ids';

export function buildGrowersHelpTourSteps(): DriveStep[] {
    return [
        {
            element: `#${GROWERS_HELP_TOUR_IDS.DETAIL}`,
            popover: {
                title: _('Detalle de tus Productores'),
                description: _(
                    'Acá vas a ver cuántos productores tenés activos, la superficie total que manejás y cómo se distribuye por cultivo en la campaña vigente.'
                ),
            },
        },
        {
            element: `#${GROWERS_HELP_TOUR_IDS.EXPERT}`,
            popover: {
                title: _('{{appName}} trabaja con vos', { appName: settings.app.name }),
                description: _(
                    'A medida que cargues productores y lotes, vas a recibir sugerencias automáticas: cuándo visitar a un productor, qué lotes están en momento crítico y qué acción tomar.'
                ),
            },
        },
        {
            element: `#${GROWERS_HELP_TOUR_IDS.TABS}`,
            popover: {
                title: _('Gestión de tus productores'),
                description: _(
                    'Seguí la campaña activa de cada productor, revisá reportes históricos de rendimiento o consultá las recomendaciones que generaste. Todo desde el mismo lugar.'
                ),
            },
            onHighlighted: (_element, _step, opts) => {
                opts.driver.setConfig({ ...opts.driver.getConfig(), stagePadding: 0 });
            },
            onDeselected: (_element, _step, opts) => {
                opts.driver.setConfig({ ...opts.driver.getConfig(), stagePadding: 4 });
            },
        },
        {
            element: `#${GROWERS_HELP_TOUR_IDS.ADD_GROWER}`,
            popover: {
                title: _('Empezá agregando tu primer productor'),
                description: _(
                    'Agregá un productor y cargá sus lotes manualmente o solicitá que comparta sus lotes de Fieldview.'
                ),
                side: 'bottom',
                align: 'start',
            },
            onHighlighted: (element, _step, opts) => {
                element?.scrollIntoView({ block: 'center', inline: 'nearest' });
                requestAnimationFrame(() => {
                    opts.driver.refresh?.();
                });
            },
        },
    ];
}

export function useGrowersHelpTour(options: {
    /** Switches to "Campaña actual" so tour anchors exist (auto-start + manual). */
    onEnsureCurrentCampaignTab: () => void;
    /** RV user node loaded — manual tour must not start without it. */
    canStartManualTour: boolean;
}) {
    const { onEnsureCurrentCampaignTab, canStartManualTour } = options;

    const growersHelpTourSteps = useMemo(() => buildGrowersHelpTourSteps(), []);

    const prepareGrowersHelpTour = useCallback(() => {
        onEnsureCurrentCampaignTab();
    }, [onEnsureCurrentCampaignTab]);

    const { startTour } = useManualDriver(growersHelpTourSteps, {
        popoverTag: _('MIS PRODUCTORES'),
        mixpanelSection: 'growers',
        showHelpCenterLink: true,
    });

    const startHelpTourFromIcon = useCallback(() => {
        if (!canStartManualTour) return;

        onEnsureCurrentCampaignTab();
        const appContent = document.querySelector('#app-content');
        if (appContent) {
            (appContent as HTMLElement).scrollTop = 0;
        }
        window.setTimeout(() => {
            startTour();
        }, 200);
    }, [canStartManualTour, onEnsureCurrentCampaignTab, startTour]);

    return {
        growersHelpTourSteps,
        prepareGrowersHelpTour,
        startHelpTourFromIcon,
    };
}
